import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * GeoContext — single source of truth for the caller's country + preferred
 * DarePass currency. Populated once on app mount and shared across every
 * page (Landing hero copy, Pricing card, footer/legal links, etc.).
 *
 * Shape:
 *   country       ISO-3166 alpha-2 code (e.g. "IN", "US")
 *   isIndia       true when country === "IN"
 *   currency      "INR" (India) | "USD" (rest of the world)
 *   darepassPrice numeric price in the local currency
 *   ready         true after the /api/geo call resolves (or times out)
 */
const GeoContext = createContext({
  country: "IN",
  isIndia: true,
  currency: "INR",
  darepassPrice: 99,
  tier: "in",
  ready: false,
});

export function GeoProvider({ children }) {
  const [state, setState] = useState({
    country: "IN",
    isIndia: true,
    currency: "INR",
    darepassPrice: 99,
    tier: "in",
    ready: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Dev/QA override: `?geo=US` (or any ISO-3166 alpha-2) forces the
        // non-India experience for the current session. Persisted in
        // sessionStorage so tab reloads keep the override until closed.
        try {
          const url = new URL(window.location.href);
          const paramGeo = (url.searchParams.get("geo") || "").toUpperCase();
          if (paramGeo === "RESET") {
            sessionStorage.removeItem("__geo_override");
          } else if (paramGeo && /^[A-Z]{2}$/.test(paramGeo)) {
            sessionStorage.setItem("__geo_override", paramGeo);
          }
        } catch {}
        const override = (() => {
          try { return sessionStorage.getItem("__geo_override"); } catch { return null; }
        })();
        if (override && /^[A-Z]{2}$/.test(override)) {
          const res = await api.get(`/subscription/darepass/config?country=${override}`).catch(() => null);
          const d = res?.data || {};
          if (!cancelled) {
            setState({
              country: override,
              isIndia: override === "IN",
              currency: d.currency || (override === "IN" ? "INR" : "USD"),
              darepassPrice: typeof d.price === "number" ? d.price : (override === "IN" ? 99 : 2.99),
              tier: d.tier || (override === "IN" ? "in" : "global"),
              ready: true,
            });
            return;
          }
        }

        const res = await api.get("/geo");
        if (cancelled) return;
        const d = res?.data;
        // Defensive: Cloudflare Pages SPA fallback may serve index.html with
        // status 200 if the frontend is deployed without REACT_APP_BACKEND_URL.
        // Only apply the payload when it looks like our real geo response.
        const validGeo =
          d && typeof d === "object" && typeof d.country === "string" && /^[A-Z]{2}$/.test(d.country);
        if (!validGeo) {
          // keep India default, just flag ready
          setState((s) => ({ ...s, ready: true }));
          return;
        }
        setState({
          country: d.country,
          isIndia: !!d.is_india,
          currency: d.currency || "INR",
          darepassPrice: typeof d.darepass_price === "number" ? d.darepass_price : 99,
          tier: d.tier || "in",
          ready: true,
        });
      } catch {
        // Network / geo lookup failed → keep the safe INR default but flag ready
        if (!cancelled) setState((s) => ({ ...s, ready: true }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return <GeoContext.Provider value={state}>{children}</GeoContext.Provider>;
}

export function useGeo() {
  return useContext(GeoContext);
}
